import { UseFormReturn } from "@lya-protect/lya-protect-form-library/dist/Exports/reactHookForm.exports";
import { gatewayAPI } from "@src/GatewayAPI";
import {
  ERROR_BIRTHDATE,
  validBirth,
} from "@src/Steps/simulate/birthDate/BirthDate";
import { useCallback } from "react";
import { Card } from "@src/components/card/Card";
import { InfoCard } from "@src/components/infoCard/InfoCard";
import { NavButton } from "@src/components/navButton/NavButton";
import "./beneficiaires.scss";
import {
  Form,
  InputButtonGroup,
  InputCheckbox,
  InputDate,
  InputSelect,
  InputText,
  useFormConfig,
} from "@lya-protect/lya-protect-form-library/dist/FormAPI";
import {
  Button,
  Emphasis,
  FormAction,
  FormField,
  FormGrid,
  FormRow,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import { Stepbar } from "@src/components/stepbar/Stepbar";
import { MicroStep, StepProps } from "@src/Steps/step/Steps";
import { zod } from "@lya-protect/lya-protect-form-library/dist/Exports";
import { OuiNonOptions, PersonneType } from "@src/utils";
import { ReactComponent as AddSvg } from "@src/svg/add.svg";
import { ReactComponent as BinSvg } from "@src/svg/bin.svg";

const getOrdinalBeneficiaire = (index: number): string => {
  const number = index + 1;
  const ordinals: { [key: number]: string } = {
    1: "Premier",
    2: "Deuxième",
    3: "Troisième",
  };
  return ordinals[number] || `${number}ème`;
};

const getKinshipDetailOptions = (
  kinship: string | undefined
): { label: string; value: string }[] | null => {
  switch (kinship) {
    case "PARENT:ROLE_RELATION_TYPE":
      return [
        { label: "Mère", value: "Mère" },
        { label: "Père", value: "Père" },
      ];
    case "FRATERIE:ROLE_RELATION_TYPE":
      return [
        { label: "Sœur", value: "Sœur" },
        { label: "Frère", value: "Frère" },
      ];
    case "FAMILLE:ROLE_RELATION_TYPE":
      return null;
    default:
      return [];
  }
};

const beneficiairePhysiqueSchema = zod.object({
  type: zod.literal(PersonneType.personnePhysique),
  familyName: zod.string({ required_error: "Champ requis" }).min(1),
  usageName: zod.string().optional(),
  firstName: zod.string({ required_error: "Champ requis" }).min(1),
  birthDate: zod
    .date({ required_error: "Champ requis" })
    .refine(validBirth, ERROR_BIRTHDATE)
    .optional(),
  birthZipcode: zod.string({ required_error: "Champ requis" }).min(1),
  birthPlace: zod.string({ required_error: "Champ requis" }).min(1),
  kinship: zod
    .enum([
      "CONJOINT:ROLE_RELATION_TYPE",
      "ENFANT:ROLE_RELATION_TYPE",
      "PARENT:ROLE_RELATION_TYPE",
      "FRATERIE:ROLE_RELATION_TYPE",
      "FAMILLE:ROLE_RELATION_TYPE",
    ])
    .optional(),
  address: zod.string({ required_error: "Champ requis" }).min(1),
  addressComplement: zod.string().optional(),
  zipcode: zod.string({ required_error: "Champ requis" }).min(1),
  city: zod.string({ required_error: "Champ requis" }).min(1),
  phone: zod
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        const cleaned = val.replace(/[\s.-]/g, "");
        return /^(0[1-7,9]\d{8}|(?:\+33|0033)[1-5,9]\d{8})$/.test(cleaned);
      },
      { message: "Format téléphone fixe invalide" }
    ),
  email: zod.string().email("Email invalide").optional().or(zod.literal("")),
  kinshipDetail: zod.string().optional(),
});

const schema = zod
  .object({
    hasBeneficiaire: zod.boolean({ required_error: "Champ requis" }).optional(),
    isPersonnePhysique: zod
      .boolean({ required_error: "Champ requis" })
      .optional(),
    hasEqualShares: zod.boolean().optional(),
    beneficiaries: zod.array(beneficiairePhysiqueSchema).default([]).optional(),
    noPersonnePhysique: zod
      .boolean({ required_error: "Champ requis" })
      .optional(),
  })
  .refine(
    (data) =>
      !data.isPersonnePhysique ||
      (data.beneficiaries && data.beneficiaries.length > 0),
    {
      message: "Au moins un bénéficiaire est requis",
      path: ["beneficiaires"],
    }
  )
  .refine(
    (data) =>
      data.isPersonnePhysique !== false || data.noPersonnePhysique === true,
    {
      message: "Champ requis",
      path: ["noPersonnePhysique"],
    }
  );

export type BeneficiairesData = zod.infer<typeof schema>;
export type Beneficiaire = zod.infer<typeof beneficiairePhysiqueSchema>;
type BeneficiairesProps = StepProps<BeneficiairesData>;

function BeneficiaireCard({
  index,
  onRemove,
  formConfig,
}: {
  index: number;
  onRemove: () => void;
  formConfig: UseFormReturn<BeneficiairesData>;
}) {
  const zipcode = formConfig.watch(`beneficiaries.${index}.zipcode`);
  const beneficiaryRelationCode = formConfig.watch(
    `beneficiaries.${index}.kinship`
  );

  function getRelationDetails() {
    switch (beneficiaryRelationCode) {
      case "PARENT:ROLE_RELATION_TYPE":
      case "FRATERIE:ROLE_RELATION_TYPE":
        return (
          <FormField>
            <InputSelect
              name={`beneficiaries.${index}.kinshipDetail`}
              label="Préciser"
              placeholder="Sélectionner"
              options={getKinshipDetailOptions(beneficiaryRelationCode) || []}
              required
            />
          </FormField>
        );
      case "FAMILLE:ROLE_RELATION_TYPE":
        return (
          <FormField>
            <InputText
              name={`beneficiaries.${index}.kinshipDetail`}
              label="Préciser"
              placeholder="Veuillez spécifier"
              required
            />
          </FormField>
        );
      default:
        return null;
    }
  }

  return (
    <Card>
      <div className="beneficiaire-card__header">
        <h3>
          <Emphasis>{`${getOrdinalBeneficiaire(index)} bénéficiaire`}</Emphasis>
        </h3>
        <Button
          type="button"
          variant="text"
          disabled={index === 0}
          onClick={onRemove}
          icon={<BinSvg />}
          iconVariant="left"
        >
          Retirer
        </Button>
      </div>
      <>
        <FormRow>
          <FormField>
            <InputText
              name={`beneficiaries.${index}.familyName`}
              label="Nom de famille"
              required
            />
          </FormField>
          <FormField>
            <InputText
              name={`beneficiaries.${index}.usageName`}
              label="Nom d'usage"
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <InputText
              name={`beneficiaries.${index}.firstName`}
              label="Prénom"
              required
            />
          </FormField>
          <FormField>
            <InputDate
              name={`beneficiaries.${index}.birthDate`}
              label={
                <>
                  Date de naissance <Emphasis>*</Emphasis>
                </>
              }
              required
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <InputText
              name={`beneficiaries.${index}.birthZipcode`}
              label="Code postal de naissance"
              required
              placeholder="Ex: 01000"
            />
          </FormField>
          <FormField>
            <InputText
              name={`beneficiaries.${index}.birthPlace`}
              label="Lieu de naissance"
              required
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <InputSelect
              name={`beneficiaries.${index}.kinship`}
              label="Lien"
              placeholder="Sélectionner"
              options={[
                {
                  label: "Conjoint",
                  value: "CONJOINT:ROLE_RELATION_TYPE",
                },
                { label: "Enfant", value: "ENFANT:ROLE_RELATION_TYPE" },
                {
                  label: "Parent",
                  value: "PARENT:ROLE_RELATION_TYPE",
                },
                {
                  label: "Fraterie",
                  value: "FRATERIE:ROLE_RELATION_TYPE",
                },
                {
                  label: "Autre",
                  value: "FAMILLE:ROLE_RELATION_TYPE",
                },
              ]}
              onChange={(nextValue) => {
                if (!nextValue) return;

                if (
                  nextValue === "FRATERIE:ROLE_RELATION_TYPE" ||
                  nextValue === "PARENT:ROLE_RELATION_TYPE"
                )
                  formConfig.setValue(
                    `beneficiaries.${index}.kinshipDetail`,
                    ""
                  );
                formConfig.setValue(
                  `beneficiaries.${index}.kinship`,
                  nextValue as
                    | "CONJOINT:ROLE_RELATION_TYPE"
                    | "ENFANT:ROLE_RELATION_TYPE"
                    | "PARENT:ROLE_RELATION_TYPE"
                    | "FRATERIE:ROLE_RELATION_TYPE"
                    | "FAMILLE:ROLE_RELATION_TYPE"
                );
              }}
              required
            />
          </FormField>
          {getRelationDetails()}
        </FormRow>
      </>
      <>
        <FormRow>
          <FormField>
            <InputText
              name={`beneficiaries.${index}.address`}
              label="Adresse"
              required
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <InputText
              name={`beneficiaries.${index}.addressComplement`}
              label="Complément d'adresse"
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <InputText
              name={`beneficiaries.${index}.zipcode`}
              label="Code postal"
              required
              placeholder="Ex: 75013"
              autoComplete={{
                displayLabel: (result: string) =>
                  `${zipcode ?? ""} - ${result}`,
                fetchResults: async () => {
                  if (!zipcode || zipcode.length !== 5) return [];
                  return gatewayAPI.axios
                    .get<string[]>(`geo/communes/search/${zipcode}`)
                    .then((res) => res.data)
                    .catch(() => []);
                },
                onSelect: (newCity) => {
                  formConfig.setValue(`beneficiaries.${index}.city`, newCity, {
                    shouldValidate: true,
                    shouldTouch: true,
                  });
                },
                focusAfterSelect: `beneficiaries.${index}.city`,
              }}
            />
          </FormField>
          <FormField>
            <InputText
              name={`beneficiaries.${index}.city`}
              label="Ville"
              required
              placeholder="Ex: Paris"
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <InputText
              name={`beneficiaries.${index}.phone`}
              label="Téléphone"
              placeholder="Ex: 06 00 00 00 00"
            />
          </FormField>
          <FormField>
            <InputText
              name={`beneficiaries.${index}.email`}
              label="Email"
              placeholder="Ex: prenom.nom@mail.com"
            />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField>
            <InfoCard>
              <p>
                Nous vous recommandons de fournir le numéro de téléphone et
                l&apos;adresse email du bénéficiaire désigné afin de faciliter
                le traitement de votre dossier.
              </p>
            </InfoCard>
          </FormField>
        </FormRow>
      </>
    </Card>
  );
}

export function Beneficiaires({
  changeStep,
  data,
  setData,
}: BeneficiairesProps) {
  const formConfig = useFormConfig<typeof schema, BeneficiairesData>({
    schema,
    defaultValues: data,
    mode: "onChange",
  });

  const isPersonnePhysique = formConfig.watch("isPersonnePhysique");
  const hasBeneficiaire = formConfig.watch("hasBeneficiaire");
  const beneficiaires = formConfig.watch("beneficiaries") || [];
  const emptyBeneficiaires = {
    type: PersonneType.personnePhysique,
    familyName: "",
    firstName: "",
    birthZipcode: "",
    birthPlace: "",
    kinship: "ROLE_RELATION_TYPE:CONJOINT",
    address: "",
    zipcode: "",
    city: "",
    email: "",
    kinshipDetail: "",
  } as unknown as Beneficiaire;

  const handleAddBeneficiaire = useCallback(() => {
    formConfig.setValue("beneficiaries", [
      ...beneficiaires,
      emptyBeneficiaires,
    ]);
  }, [beneficiaires, formConfig]);

  const handleRemoveBeneficiaire = useCallback(
    (index: number) => {
      const copy = [...beneficiaires];
      copy.splice(index, 1);
      formConfig.setValue("beneficiaries", copy);
    },
    [beneficiaires, formConfig]
  );

  const normalizeBeneficiaire = (beneficiaire: Beneficiaire): Beneficiaire => {
    if (beneficiaire.birthDate)
      return {
        ...beneficiaire,
        birthDate: new Date(
          beneficiaire.birthDate.getFullYear(),
          beneficiaire.birthDate.getMonth(),
          beneficiaire.birthDate.getDate(),
          12,
          0,
          0
        ),
      };
    return beneficiaire;
  };

  const handleSubmit = (submittedData: BeneficiairesData) => {
    const normalizedData = {
      ...submittedData,
      beneficiaries: (submittedData.beneficiaries || []).map(
        normalizeBeneficiaire
      ),
    };
    setData({
      ...data,
      ...normalizedData,
    });
    changeStep("CONSENT_SOUSCRIPTION", "forward");
  };

  return (
    <div className="beneficiaire-step">
      <div className="stepper__centered-container">
        <div className="stepper__centered-content step__grid step__grid--padding-bottom">
          <Stepbar activeMicroStep={MicroStep.PersonalInformation} />
          <NavButton
            onClick={() => {
              changeStep("POLITIQUEMENT_EXPOSEE", "backward");
            }}
          >
            Précédent
          </NavButton>
          <Form
            className="beneficiaire-step__form"
            formConfig={formConfig}
            onSubmit={handleSubmit}
          >
            <FormGrid>
              <FormRow>
                <FormField className="beneficiaire-step__form-gap">
                  <InputButtonGroup<BeneficiairesData, boolean>
                    label={
                      <>
                        Souhaitez-vous faire une{" "}
                        <Emphasis>désignation de bénéficiaire(s)</Emphasis> en
                        cas de décès accidentel ?
                      </>
                    }
                    variant="outline"
                    size="large"
                    name="hasBeneficiaire"
                    required
                    onChange={(value) => {
                      formConfig.setValue("hasBeneficiaire", value);

                      if (!value) {
                        formConfig.setValue("beneficiaries", []);
                        formConfig.setValue("isPersonnePhysique", undefined);
                        formConfig.setValue("hasEqualShares", undefined);
                      }
                    }}
                    options={OuiNonOptions}
                  />
                  {hasBeneficiaire === undefined && (
                    <InfoCard>
                      <div>
                        En cas de décès accidentel, le contrat Paxivie prévoit
                        le versement d&apos;une allocation temporaire décès si
                        les conditions contractuelles sont remplies. A défaut de
                        désignation, la prestation sera versée conformément à la
                        clause-type prévue au contrat Paxivie (article 11 du
                        règlement Paxivie), à savoir aux héritiers. Néanmoins,
                        durant toute la vie du contrat, une désignation peut
                        être réalisée et/ou modifiée, à condition que celle-ci
                        soit adressée à la mutuelle Tutélaire avant le décès du
                        membre participant.
                        <br /> Un décès est dit accidentel lorsque sa causalité
                        accidentelle est directe, certaine et déterminante,
                        résultant d&apos;une cause extérieure imprévisible.
                      </div>
                    </InfoCard>
                  )}
                  {hasBeneficiaire === false && (
                    <InfoCard>
                      <div>
                        A défaut de désignation, la prestation sera versée
                        conformément à la clause-type prévue au contrat Paxivie
                        (article 11 du règlement Paxivie), à savoir aux
                        héritiers. <br /> <br />
                        Néanmoins, durant toute la vie du contrat, une
                        désignation peut être réalisée et/ou modifiée, à
                        condition que celle-ci soit adressée à la mutuelle
                        Tutélaire avant le décès du membre participant.
                      </div>
                    </InfoCard>
                  )}
                  {hasBeneficiaire === true && (
                    <InfoCard>
                      <div>
                        Afin de prévenir tout litige ultérieur, les services de
                        Tutélaire procèdent à une vérification de la
                        recevabilité de la présente désignation de
                        bénéficiaire(s) en vue de son exploitation future.
                        <br /> <br />
                        En cas d’irrecevabilité, une nouvelle désignation sera
                        demandée par courrier ou par courriel, lequel précisera
                        le motif du rejet. Tant que la désignation de
                        bénéficiaire(s) nécessite des corrections pour permettre
                        sa bonne exploitation, la dernière désignation de
                        bénéficiaire(s) valide enregistrée par Tutélaire
                        s&apos;appliquera ou à défaut la clause type prévue au
                        contrat Paxivie.
                      </div>
                    </InfoCard>
                  )}
                </FormField>
              </FormRow>
              {hasBeneficiaire === true && (
                <FormRow>
                  <FormField className="beneficiaire-step__form-gap">
                    <InputButtonGroup<BeneficiairesData, boolean>
                      label={
                        <>
                          Quel type de <Emphasis>bénéficiaire(s)</Emphasis>{" "}
                          souhaitez-vous désigner en cas de décès accidentel ?
                        </>
                      }
                      variant="outline"
                      size="large"
                      name="isPersonnePhysique"
                      required
                      onChange={(value) => {
                        formConfig.setValue("isPersonnePhysique", value);
                        if (
                          value &&
                          (formConfig.getValues().beneficiaries?.length ??
                            0) === 0
                        ) {
                          formConfig.setValue("beneficiaries", [
                            emptyBeneficiaires,
                          ]);
                        }
                        if (!value) {
                          formConfig.setValue("beneficiaries", []);
                          formConfig.setValue("hasEqualShares", undefined);
                          formConfig.setValue("noPersonnePhysique", undefined);
                        }
                      }}
                      options={[
                        {
                          label: "Personne Physique",
                          value: true,
                          row: 1,
                        },
                        {
                          label: "Personne Morale (entreprise, association)...",
                          value: false,
                          row: 1,
                        },
                      ]}
                    />
                    {isPersonnePhysique === false && (
                      <InfoCard>
                        <h3>
                          Vous ne pouvez pas effectuer une désignation de
                          bénéficiaire Personne morale en cas de décès en ligne.
                        </h3>
                        <br />
                        <div>
                          Merci de rejoindre nos conseillers par téléphone :
                          <br />
                          - du lundi au vendredi
                          <br />
                          - de 9h00 à 12h30 et de 13h30 à 17h au : 0 969 398 399
                          (appel non surtaxé).
                          <br />
                          <br />
                          Vous pouvez poursuivre le parcours d’adhésion et
                          effectuer la désignation du bénéficiaire Personne
                          morale a posteriori.
                        </div>
                      </InfoCard>
                    )}
                  </FormField>
                </FormRow>
              )}
              {isPersonnePhysique === true && hasBeneficiaire === true && (
                <div className="beneficiaire-step__has-beneficiaires">
                  {beneficiaires.map((_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <FormRow key={`beneficiaire-${index}`}>
                      <FormField>
                        <BeneficiaireCard
                          index={index}
                          formConfig={formConfig}
                          onRemove={() => handleRemoveBeneficiaire(index)}
                        />
                      </FormField>
                    </FormRow>
                  ))}

                  <Button
                    type="button"
                    variant="text"
                    disabled={beneficiaires.length === 3}
                    onClick={handleAddBeneficiaire}
                    icon={<AddSvg />}
                    iconVariant="left"
                  >
                    Ajouter un(e) bénéficiaire
                  </Button>
                  <InfoCard>
                    <h3>
                      Par défaut, l&apos;allocation temporaire décès est versée
                      par parts égales à chaque bénéficiaire désigné.
                    </h3>
                    <br />
                    <InputCheckbox
                      name="hasEqualShares"
                      label="Si vous souhaitez que cette allocation soit versée en totalité au premier des bénéficiaires survivants dans l'ordre de désignation, merci de cocher cette case."
                    />
                  </InfoCard>
                </div>
              )}{" "}
              {isPersonnePhysique === false && (
                <FormRow>
                  <FormField>
                    <InputCheckbox
                      name="noPersonnePhysique"
                      required
                      label="J'ai compris que pour désigner une personne morale en tant que bénéficiaire du contrat en cas de décès, je vois prendre contact avec les conseillers de Tutélaire."
                    />
                  </FormField>
                </FormRow>
              )}
              <FormAction>
                <Button
                  type="submit"
                  disabled={
                    hasBeneficiaire === undefined ||
                    (hasBeneficiaire === true &&
                      (isPersonnePhysique === undefined ||
                        !formConfig.formState.isValid)) ||
                    formConfig.formState.isSubmitting ||
                    formConfig.formState.isSubmitSuccessful
                  }
                >
                  Suivant
                </Button>
              </FormAction>
            </FormGrid>
          </Form>
        </div>
      </div>
    </div>
  );
}
